import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";

import { openSalesInquiry } from "@/components/marketing/SalesInquiryDialog";
import {
  ACCENT,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
  ACCENT_DEEP,
} from "@/components/marketing/tokens";
import { ArrowIcon } from "@/components/marketing/MarketingIcons";
import { ActionButton, SectionContainer } from "./HomePrimitives";
import { STICKY_TRIGGER_ID } from "./StickyFreeBar";
import { HOME_PRODUCTS } from "./homeContent";
import { BODY_SMALL, HEADING_2 } from "./homeType";

/**
 * Three across at 1440 and 1280, two from 992 down, one below 480, and not a
 * media query anywhere. The track is `auto-fit` over a 21rem minimum, so the
 * column count follows the content box; that is why this section's height jumps
 * between 1280 and 992 without crossing one of the site's steps.
 */
const PRODUCT_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 21rem), 1fr))",
  gap: "8px",
  width: 1,
};

export default function ProductSection() {
  return (
    <SectionContainer padding="both">
      {/*
        The sticky bar's scroll sentinel, in the position the published page
        gives it. It draws nothing; `StickyFreeBar` observes it.
      */}
      <Box
        id={STICKY_TRIGGER_ID}
        aria-hidden
        sx={{
          // Anchored to the section rather than to the content column: its
          // containing block is the section, so a static left would put a
          // full-width box past the right edge and give the page a scrollbar.
          position: "absolute",
          inset: "142.8px auto auto 0",
          width: 1,
          height: "560px",
          // It lies over the cards, so without this it eats every hover and
          // click meant for them.
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          rowGap: "32px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            rowGap: "8px",
            width: "70%",
            mx: "auto",
            textAlign: "center",
            [NOT_WIDE]: { width: 1 },
          }}
        >
          <Typography component="h2" sx={HEADING_2}>
            {HOME_PRODUCTS.heading}
          </Typography>
          <Typography component="p" sx={BODY_SMALL}>
            {HOME_PRODUCTS.body}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: 1,
          }}
        >
          <Box sx={PRODUCT_GRID}>
            {HOME_PRODUCTS.cards.map((card) => (
              <Box
                key={card.title}
                sx={{
                  display: "grid",
                  // The cards level with the tallest in their row, and the
                  // published one gives all of that slack to the copy: the
                  // icon stays put, the price and button stay at the foot.
                  gridTemplateRows: "auto 1fr auto auto",
                  gap: "20px",
                  p: "24px",
                  borderRadius: "16px",
                  border: `1px solid ${marketingPalette.outline}`,
                  [TINY]: { gap: "16px", p: "20px" },
                }}
              >
                <Box
                  component="img"
                  src={card.icon}
                  alt=""
                  loading="lazy"
                  sx={{ width: "2.75rem", height: "2.75rem" }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4.8px",
                  }}
                >
                  <Box
                    sx={{
                      color: marketingPalette.ink,
                      fontSize: 17,
                      lineHeight: "25.5px",
                      fontWeight: 500,
                      [MID]: { fontSize: 16, lineHeight: "24px" },
                      [TINY]: { fontSize: 15, lineHeight: "21px" },
                    }}
                  >
                    {card.title}
                  </Box>
                  <Box
                    sx={{
                      opacity: 0.8,
                      fontSize: "0.88rem",
                      lineHeight: "150%",
                      textWrap: "pretty",
                    }}
                  >
                    {card.body}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "4.8px",
                  }}
                >
                  <Box
                    sx={{
                      color: marketingPalette.ink,
                      fontSize: "1.5rem",
                      lineHeight: "24px",
                      fontWeight: 500,
                    }}
                  >
                    {card.price}
                  </Box>
                  <Box sx={{ fontSize: "0.81rem", lineHeight: "18.144px" }}>
                    {card.period}
                  </Box>
                </Box>
                <ActionButton cta={card.cta} fill={card.accent} arrow={false} />
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              width: 1,
              py: "10.08px",
              borderRadius: "16px",
              backgroundColor: marketingPalette.bannerTint,
              [MID]: { flexDirection: "column" },
              [TINY]: { px: "8px" },
            }}
          >
            <Typography
              component="p"
              sx={{
                color: marketingPalette.ink,
                fontSize: 16,
                lineHeight: "24px",
                [TINY]: { lineHeight: "22.4px" },
              }}
            >
              {HOME_PRODUCTS.banner.title}
            </Typography>
            {/*
              A button, like the header's Book a Call: the published control is
              an `href="#"` that a script turns into the same sales form, so a
              link would go nowhere.
            */}
            <ButtonBase
              type="button"
              aria-haspopup="dialog"
              onClick={openSalesInquiry}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                height: "2.6rem",
                px: "0.8rem",
                borderRadius: "8px",
                // A native `button` takes the UA font, not the page's; the
                // published control is an anchor and keeps the body type.
                fontFamily: "inherit",
                fontSize: 16,
                lineHeight: "24px",
                color: ACCENT,
                transition: "all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
                "&:hover": {
                  backgroundColor: "rgba(26, 179, 148, 0.1)",
                  color: ACCENT_DEEP,
                },
                [NOT_WIDE]: {
                  height: "3rem",
                  px: "1rem",
                  backgroundColor: "rgba(26, 179, 148, 0.1)",
                },
                [MID]: { width: 1 },
              }}
            >
              {HOME_PRODUCTS.banner.label}
              {/* `.pricing-card-button_icon.hide_mob`: gone on a phone. */}
              <Box sx={{ display: "flex", [TINY]: { display: "none" } }}>
                <ArrowIcon />
              </Box>
            </ButtonBase>
          </Box>
        </Box>
      </Box>
    </SectionContainer>
  );
}
